import { createSlice } from '@reduxjs/toolkit';
import { SysConnectorCatalogDto, SysConnectorDto } from '../../services/api/connectorService';
import {
  fetchConnectorCatalog,
  fetchConnectors,
  createConnector,
  updateConnector,
  deleteConnector,
  activateConnector,
  deactivateConnector,
} from '../thunks/connectorThunks';

export interface ConnectorState {
  catalog: SysConnectorCatalogDto[];
  connectors: SysConnectorDto[];
  loading: boolean;
  catalogLoading: boolean;
  error: string | null;
}

const initialState: ConnectorState = {
  catalog: [],
  connectors: [],
  loading: false,
  catalogLoading: false,
  error: null,
};

const connectorSlice = createSlice({
  name: 'connector',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchConnectorCatalog
    builder
      .addCase(fetchConnectorCatalog.pending, (state) => {
        state.catalogLoading = true;
        state.error = null;
      })
      .addCase(fetchConnectorCatalog.fulfilled, (state, action) => {
        state.catalogLoading = false;
        state.catalog = action.payload;
      })
      .addCase(fetchConnectorCatalog.rejected, (state, action) => {
        state.catalogLoading = false;
        state.error = action.payload as string;
      });

    // fetchConnectors
    builder
      .addCase(fetchConnectors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConnectors.fulfilled, (state, action) => {
        state.loading = false;
        state.connectors = action.payload;
      })
      .addCase(fetchConnectors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // createConnector
    builder
      .addCase(createConnector.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createConnector.fulfilled, (state, action) => {
        state.loading = false;
        state.connectors.push(action.payload);
      })
      .addCase(createConnector.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // updateConnector
    builder
      .addCase(updateConnector.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateConnector.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.connectors.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.connectors[idx] = action.payload;
      })
      .addCase(updateConnector.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // deleteConnector
    builder
      .addCase(deleteConnector.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConnector.fulfilled, (state, action) => {
        state.loading = false;
        state.connectors = state.connectors.filter((c) => c.id !== action.payload);
      })
      .addCase(deleteConnector.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // activateConnector
    builder
      .addCase(activateConnector.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateConnector.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.connectors.findIndex((c) => c.id === action.payload);
        if (idx !== -1) state.connectors[idx] = { ...state.connectors[idx], isActive: true, status: 'active' };
      })
      .addCase(activateConnector.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // deactivateConnector
    builder
      .addCase(deactivateConnector.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deactivateConnector.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.connectors.findIndex((c) => c.id === action.payload);
        if (idx !== -1) state.connectors[idx] = { ...state.connectors[idx], isActive: false, status: 'inactive' };
      })
      .addCase(deactivateConnector.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = connectorSlice.actions;
export default connectorSlice.reducer;
